/**
 * Hook to track impressions for sponsored polls.
 * Uses Intersection Observer to detect when a sponsored poll is viewed.
 * Only counts one impression per poll per session.
 */

import { useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

// Track which polls have been counted this session
const impressedPollIds = new Set<string>();

export function useSponsoredImpression(pollId: string, isSponsored: boolean) {
  const hasTracked = useRef(false);
  const elementRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Only track sponsored polls that haven't been tracked this session
    if (!isSponsored || hasTracked.current || impressedPollIds.has(pollId)) {
      return;
    }

    const element = elementRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // Only count impression when poll is at least 50% visible
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            if (!hasTracked.current && !impressedPollIds.has(pollId)) {
              hasTracked.current = true;
              impressedPollIds.add(pollId);
              
              // Increment impression count in database
              // Use type assertion since the RPC function was just added
              (supabase.rpc as any)('increment_sponsor_impressions', { poll_id: pollId })
                .then(({ error }: { error: any }) => {
                  if (error) {
                    console.error('Failed to track impression:', error);
                    // Remove from set so it can be retried
                    impressedPollIds.delete(pollId);
                    hasTracked.current = false;
                  }
                });
            }
            
            // Stop observing after tracking
            observer.disconnect();
          }
        });
      },
      { threshold: 0.5 }
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [pollId, isSponsored]);

  return elementRef;
}
